// withFetchTranslation.js
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import withRouter from '../component/WithRouter.jsx';

import getUserData from '../selector/getUserData.js';
import getOperationData from '../selector/getOperationData.js';
import getMeData from '../selector/getMeData.js';
import getMessageDataWithVariable, {
  VariableName,
} from '../selector/getMessageDataWithVariable.js';
import getNetworkingData from '../selector/getNetworkingData.js';

import fetchMessageTranslation, {
  fetchMessageTranslationCore,
} from '../action/fetchMessageTranslation.js';
import addMessageTranslationThrottleId from '../action/addMessageTranslationThrottleId.js';

/**
 * With fetch translation
 * @param {ReactComponent} BaseComponent - base component.
 */
const withFetchTranslation = BaseComponent => {
  class FetchTranslation extends React.Component {
    constructor(props) {
      super(props);
      const {
        messageId,
        userId,
        language,
        staticContext,
        fetchMessageTranslationForSSR,
      } = props;
      this.nextTicks = [];
      if (staticContext.actions) {
        if (this.getShouldFetchMessageTranslation()) {
          staticContext.actions.push({
            function: fetchMessageTranslationForSSR,
            argumentObject: {
              messageId,
              language,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
        if (this.getShouldFetchUserTranslation()) {
          staticContext.actions.push({
            function: fetchMessageTranslationForSSR,
            argumentObject: {
              userId,
              language,
              shouldSkipCache: true,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
      }
    }
    getShouldFetchUserTranslation = () => {
      const {
        userId,
        originalBiography,
        language,
        isUserTranslationFetched,
        isUserTranslationFetching,
        shouldFetchUserTranslation,
      } = this.props;
      return !!(
        userId &&
        originalBiography &&
        language &&
        !isUserTranslationFetched &&
        !isUserTranslationFetching &&
        shouldFetchUserTranslation
      );
    };
    getShouldFetchMessageTranslation = () => {
      const {
        messageId,
        originalTitle,
        originalCaptionText,
        language,
        isMessageTranslationFetched,
        isMessageTranslationFetching,
        shouldFetchMessageTranslation,
      } = this.props;
      return !!(
        messageId &&
        (originalCaptionText || originalTitle) &&
        language &&
        !isMessageTranslationFetched &&
        !isMessageTranslationFetching &&
        shouldFetchMessageTranslation
      );
    };
    fetchUserTranslation = () => {
      const { userId, language, fetchMessageTranslation } = this.props;
      if (this.getShouldFetchUserTranslation()) {
        this.nextTicks.push(
          setTimeout(() => fetchMessageTranslation({ userId, language }))
        );
      }
    };
    fetchMessageTranslation = () => {
      const { messageId, language, fetchMessageTranslation } = this.props;
      if (this.getShouldFetchMessageTranslation()) {
        this.nextTicks.push(
          setTimeout(() => fetchMessageTranslation({ messageId, language }))
        );
      }
    };
    componentDidMount() {
      this.fetchMessageTranslation();
      this.fetchUserTranslation();
    }
    componentDidUpdate(prevProps) {
      const {
        messageId,
        userId,
        language,
        originalTitle,
        originalCaptionText,
        originalBiography,
        shouldFetchUserTranslation,
        shouldFetchMessageTranslation,
      } = this.props;
      const isLanguageChanged = language !== prevProps.language;
      if (
        messageId !== prevProps.messageId ||
        originalCaptionText !== prevProps.originalCaptionText ||
        originalTitle !== prevProps.originalTitle ||
        shouldFetchMessageTranslation !==
          prevProps.shouldFetchMessageTranslation ||
        isLanguageChanged
      ) {
        this.fetchMessageTranslation();
      }
      if (
        userId !== prevProps.userId ||
        originalBiography !== prevProps.originalBiography ||
        shouldFetchUserTranslation !== prevProps.shouldFetchUserTranslation ||
        isLanguageChanged
      ) {
        this.fetchUserTranslation();
      }
    }
    componentWillUnmount() {
      this.nextTicks.forEach(clearTimeout);
    }
    render() {
      return <BaseComponent {...this.props} />;
    }
  }
  FetchTranslation.propTypes = {
    messageId: PropTypes.string,
    userId: PropTypes.string,
    language: PropTypes.string,
    originalTitle: PropTypes.string,
    originalCaptionText: PropTypes.string,
    originalBiography: PropTypes.string,
    isMessageTranslationFetched: PropTypes.bool,
    isMessageTranslationFetching: PropTypes.bool,
    isUserTranslationFetched: PropTypes.bool,
    isUserTranslationFetching: PropTypes.bool,
    shouldFetchUserTranslation: PropTypes.bool,
    shouldFetchMessageTranslation: PropTypes.bool,
    staticContext: PropTypes.object,
    fetchMessageTranslation: PropTypes.func,
    fetchMessageTranslationForSSR: PropTypes.func,
  };
  FetchTranslation.defaultProps = {
    messageId: '',
    userId: '',
    language: '',
    originalTitle: '',
    originalCaptionText: '',
    originalBiography: '',
    isMessageTranslationFetched: false,
    isMessageTranslationFetching: false,
    isUserTranslationFetched: false,
    isUserTranslationFetching: false,
    shouldFetchUserTranslation: false,
    shouldFetchMessageTranslation: false,
    staticContext: {},
    fetchMessageTranslation: () => null,
    fetchMessageTranslationForSSR: () => null,
  };
  const mapStateToProps = (state, { messageId, userId, i18n }) => {
    const currentLanguage = getOperationData(state, ['config'], 'language');
    const meUsername = getMeData(state, 'username');
    const language = currentLanguage || i18n.language;
    return {
      language,
      originalTitle: getMessageDataWithVariable(
        state,
        messageId,
        'originalTitle',
        `${VariableName.USERNAME}:${meUsername}`
      ),
      originalCaptionText: getMessageDataWithVariable(
        state,
        messageId,
        'originalCaptionText',
        `${VariableName.USERNAME}:${meUsername}`
      ),
      originalBiography: getUserData(state, userId, 'originalBiography'),
      isMessageTranslationFetched: getNetworkingData(
        state,
        ['translation', language, messageId],
        'isFetched'
      ),
      isMessageTranslationFetching: getNetworkingData(
        state,
        ['translation', language, messageId],
        'isFetching'
      ),
      isUserTranslationFetched: getNetworkingData(
        state,
        ['translation', language, userId],
        'isFetched'
      ),
      isUserTranslationFetching: getNetworkingData(
        state,
        ['translation', language, userId],
        'isFetching'
      ),
    };
  };

  const mapDispatchToProps = dispatch => {
    return {
      fetchMessageTranslation: ({ messageId, userId, language }) =>
        dispatch(fetchMessageTranslation({ messageId, userId, language })),
      fetchMessageTranslationForSSR: ({
        messageId,
        userId,
        language,
        httpProxyHeaders,
        shouldSkipCache,
      }) => {
        dispatch(
          addMessageTranslationThrottleId({
            messageId,
            userId,
          })
        );
        return dispatch(
          fetchMessageTranslationCore({
            language,
            httpProxyHeaders,
            shouldSkipCache,
          })
        );
      },
    };
  };
  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(withRouter(FetchTranslation)));
};

export default withFetchTranslation;
